import React, { useState, useEffect } from 'react';
import { ChevronDown } from 'lucide-react';
import { ResponsiveContainer, LineChart, Line, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import { getCrimeDisplayName } from '../../constants/crimes';
import { getStateAbbreviation } from '../../constants/locations';
import { formatNumber, formatDifference, formatRank, formatPercentile } from '../../utils/formatting';
import { getValueColor } from '../../utils/coloring';
import { useChartState } from '../../hooks/charts';
import './Charts.css';

const HistoricalTrendsChart = ({ 
  selectedLocation, 
  selectedCrime, 
  onChartOptionChange, 
  selectedChartOption, 
  selectedSource,
  compareMode
}) => {
  const [selectedChart, setSelectedChart] = useState(selectedChartOption);
  const {
    isMobile,
    windowWidth,
    isMenuOpen,
    setIsMenuOpen,
    isTouching,
    setIsTouching,
    menuRef,
    getDropdownLeftPosition
  } = useChartState();

  const { info, data } = selectedLocation;
  const locationType = info?.type?.charAt(0).toUpperCase() + info?.type?.slice(1);
  const isNation = info?.type?.toLowerCase() === 'nation';
  const isCounty = info?.type?.toLowerCase() === 'county';
  const isCity = info?.type?.toLowerCase() === 'city';
  const isCountyOrCity = isCounty || isCity;
  const crimeName = getCrimeDisplayName(selectedCrime, selectedSource);
  
  const stateAbbr = React.useMemo(() => {
    if (isCountyOrCity && selectedLocation?.info?.name?.includes(',')) {
      const stateName = selectedLocation.info.name.split(',')[1]?.trim();
      return getStateAbbreviation(stateName);
    }
    return getStateAbbreviation(selectedLocation);
  }, [isCountyOrCity, selectedLocation]);

  useEffect(() => {
    if (selectedChartOption) {
      if (isCountyOrCity) {
        const prefix = compareMode === 'state' ? stateAbbr : 'US';
        
        if (selectedChartOption.match(/^(US|[A-Z]{2})\s+/) && 
            !selectedChartOption.startsWith(prefix)) {
          const newOption = selectedChartOption.replace(/^(US|[A-Z]{2})\s+/, `${prefix} `);
          setSelectedChart(newOption);
        } else {
          setSelectedChart(selectedChartOption);
        }
      } else {
        setSelectedChart(selectedChartOption);
      }
    }
  }, [selectedChartOption, compareMode, isCountyOrCity, stateAbbr]);

  const handleTouchStart = () => {
    setIsTouching(true);
  };

  const handleTouchEnd = () => {
    setIsTouching(false);
  };  

  const chartOptions = [
    `Total Crime`,
    `Previous Total`,
    `Change from Previous Total`,
    `% Change from Previous Total`,
    `Total Crime vs Previous Total`,
    ...((!isNation) ? [
      `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Rank`,
      `${isCountyOrCity ? `${compareMode === 'state' ? stateAbbr : 'US'} ` : ''}${locationType} Percentile`
    ] : [])
  ];

  if (!selectedChart || !chartOptions.includes(selectedChart)) {
    return null;
  }

  const getChartOption = (baseOption) => {
    if (baseOption === 'Total Crime') {
      return baseOption;
    }
    
    if (isCountyOrCity) {
      const prefix = compareMode === 'state' ? stateAbbr : 'US';
      return baseOption.replace(`${locationType}`, `${prefix} ${locationType}`);
    }
    
    return baseOption;
  };

  const getTotalLocations = () => {
    if (!isCountyOrCity) {
      return data.current.total_locations || 0;
    }
    
    if (!data?.historical) {
      return compareMode === 'state' ? 
        (isCounty ? data.current.total_counties_state : data.current.total_cities_state) || data.current.total_locations : 
        (isCounty ? data.current.total_counties_national : data.current.total_cities_national) || data.current.total_locations;
    }
    
    let maxLocations = 0;
    
    data.historical.forEach(yearData => {
      const totalLocations = compareMode === 'state' ? 
        (isCounty ? yearData.total_counties_state : yearData.total_cities_state) : 
        (isCounty ? yearData.total_counties_national : yearData.total_cities_national);
      
      if (totalLocations && totalLocations > maxLocations) {
        maxLocations = totalLocations;
      }
    });
    
    if (maxLocations === 0 && data.current) {
      return compareMode === 'state' ? 
        (isCounty ? data.current.total_counties_state : data.current.total_cities_state) || data.current.total_locations : 
        (isCounty ? data.current.total_counties_national : data.current.total_cities_national) || data.current.total_locations;
    }
    
    return maxLocations;
  };

  const extractNumber = (rank) => {
    if (typeof rank !== 'string') return rank;
    const match = rank.match(/\d+/);
    return match ? parseInt(match[0], 10) : rank;
  };

  const roundNumber = (value) => {
    const magnitude = Math.pow(10, Math.floor(Math.log10(value)));
    const normalized = value / magnitude;
    const rounded = Math.ceil(normalized / 0.1) * 0.1;
    return rounded * magnitude;
  };

  const transformData = () => {
    if (!data?.historical) {
      return [];
    }

    const rawData = data.historical
      .sort((a, b) => a.year - b.year)
      .map(yearData => {
        const crimeField = `${selectedCrime.toLowerCase()}_count`;
        const total = yearData[crimeField];
        const prevTotal = yearData[`${crimeField}_prev`];

        if (total === null || total === undefined || prevTotal === null || prevTotal === undefined) return null;

        return {
          year: yearData.year,
          total,
          prevTotal,
          difference: yearData[`${selectedCrime.toLowerCase()}_count_prev_diff`],
          percentDiff: yearData[`${selectedCrime.toLowerCase()}_count_prev_diff_pct`],
          rank: isCountyOrCity && compareMode === 'state'
            ? yearData[`${selectedCrime.toLowerCase()}_state_trend_rank`]
            : isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_national_trend_rank`] : yearData[`${selectedCrime.toLowerCase()}_trend_rank`],
          rankValue: extractNumber(isCountyOrCity && compareMode === 'state'
            ? yearData[`${selectedCrime.toLowerCase()}_state_trend_rank`]
            : isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_national_trend_rank`] : yearData[`${selectedCrime.toLowerCase()}_trend_rank`]),
          percentile: isCountyOrCity && compareMode === 'state'
            ? yearData[`${selectedCrime.toLowerCase()}_state_trend_percentile`]
            : isCountyOrCity ? yearData[`${selectedCrime.toLowerCase()}_national_trend_percentile`] : yearData[`${selectedCrime.toLowerCase()}_trend_percentile`]
        };
      })
      .filter(row => row !== null);

    if (rawData.length === 0) return rawData;

    const availableYears = rawData.map(item => item.year);
    const minYear = Math.min(...availableYears);
    const maxYear = Math.max(...availableYears);
    
    const dataMap = new Map();
    rawData.forEach(item => {
      dataMap.set(item.year, item);
    });
    
    const completeData = [];
    for (let year = minYear; year <= maxYear; year++) {
      if (dataMap.has(year)) {
        completeData.push(dataMap.get(year));
      } else {
        completeData.push({
          year,
          total: null,
          prevTotal: null,
          difference: null,
          percentDiff: null,
          rank: null,
          rankValue: null,
          percentile: null
        });
      }
    }

    const nonInfinityPctDiffs = completeData
      .map(item => item.percentDiff)
      .filter(val => val !== 'Infinity' && val !== '-Infinity' && val !== null && val !== undefined);
    
    const maxPctDiff = nonInfinityPctDiffs.length > 0 ? 
      Math.max(...nonInfinityPctDiffs.map(Math.abs)) : 100;
    
    const minPctDiff = nonInfinityPctDiffs.length > 0 ? 
      Math.min(...nonInfinityPctDiffs) : -100;
    
    const infinityPctDiffValue = Math.max(100, maxPctDiff * 2);
    const negativeInfinityPctDiffValue = Math.min(-100, minPctDiff * 2);
  
    const processForChart = (value) => {
      if (value === 'Infinity') return infinityPctDiffValue;
      if (value === '-Infinity') return negativeInfinityPctDiffValue;
      return value;
    };
    
    return completeData.map(item => ({
      ...item,
      percentDiffForChart: processForChart(item.percentDiff)
    }));
  };

  const chartData = transformData();

  const getMaxValue = () => {
    switch (selectedChart) {
      case `Total Crime`:
      case `Previous Total`:
        const maxValue = selectedChart === `Total Crime` 
          ? Math.max(...chartData.map(d => d.total || 0))
          : Math.max(...chartData.map(d => d.prevTotal || 0));
        return roundNumber(maxValue);
      case `Change from Previous Total`:
      case `% Change from Previous Total`:
        const values = chartData.map(d => 
          selectedChart === `Change from Previous Total` 
            ? d.difference 
            : d.percentDiffForChart
        );
        const maxAbs = Math.max(Math.abs(Math.min(...values)), Math.max(...values));
        return roundNumber(maxAbs);
      case getChartOption(`${locationType} Rank`):
        return getTotalLocations();
      case getChartOption(`${locationType} Percentile`):
        return 100;
      default:
        return roundNumber(Math.max(
          ...chartData.map(d => Math.max(d.total || 0, d.prevTotal || 0))
        ));
    }
  };

  const getTicks = (maxVal) => {
    if (selectedChart === getChartOption(`${locationType} Rank`)) {
      return [getTotalLocations(), 
              Math.ceil(getTotalLocations()*3/4), 
              Math.ceil(getTotalLocations()/2), 
              Math.ceil(getTotalLocations()/4), 
              1];
    } else if (selectedChart === getChartOption(`${locationType} Percentile`)) {
      return [0, 25, 50, 75, 100];
    } else if (selectedChart.includes('Change')) {
      const values = chartData.map(d => 
        selectedChart === `Change from Previous Total` 
          ? d.difference 
          : d.percentDiffForChart
      );
      const minVal = Math.min(...values);
      const maxVal = Math.max(...values);
      const roundedMax = roundNumber(Math.max(Math.abs(minVal), maxVal));
      
      if (minVal < 0 && maxVal > 0) {
        return [-roundedMax, -roundedMax/2, 0, roundedMax/2, roundedMax];
      } else if (minVal < 0) {
        return [-roundedMax, -roundedMax*3/4, -roundedMax/2, -roundedMax/4, 0];
      } else {
        return [0, roundedMax/4, roundedMax/2, roundedMax*3/4, roundedMax];
      }
    } else {
      const maxValue = roundNumber(maxVal);
      return [0, maxValue/4, maxValue/2, maxValue*3/4, maxValue];
    }
  };

  const getCustomTicks = () => {
    if (!chartData || chartData.length === 0) return [];
    return chartData
      .filter(item => item.total !== null)
      .map(item => item.year)
      .sort((a, b) => a - b);
  };

  const getYAxisDomain = () => {
    switch (selectedChart) {
      case getChartOption(`${locationType} Rank`):
        const buffer = getTotalLocations() * 0.05;
        return [1 - buffer, getTotalLocations() + buffer];
      case getChartOption(`${locationType} Percentile`):
        return [-5, 105];
      case `Change from Previous Total`:
      case `% Change from Previous Total`:
        const values = chartData.map(d => 
          selectedChart === `Change from Previous Total` 
            ? d.difference 
            : d.percentDiffForChart
        );
        const minVal = Math.min(...values);
        const maxVal = Math.max(...values);
        const roundedMax = roundNumber(Math.max(Math.abs(minVal), maxVal));
        
        if (minVal < 0 && maxVal > 0) {
          return [-roundedMax, roundedMax];
        } else if (minVal < 0) {
          return [-roundedMax, 0];
        } else {
          return [0, roundedMax];
        }
      default:
        return [0, getMaxValue()];
    }
  };

  const renderCustomLegend = () => {
    const renderLegendIcon = () => {
      const lineStrokeWidth = 2;
      const dotRadius = 4; 

      switch (selectedChart) {
        case `Change from Previous Total`:
        case `% Change from Previous Total`:
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <svg width={isMobile ? '60' : '80'} height="12">
                <line
                  x1="0"
                  y1="6"
                  x2={isMobile ? '30' : '40'}
                  y2="6"
                  stroke="rgba(0, 128, 0, 0.3)"
                  strokeWidth={6}
                />
                <line
                  x1="0"
                  y1="6"
                  x2={isMobile ? '60' : '80'}
                  y2="6"
                  stroke="rgba(0, 0, 0, 0.3)"
                  strokeWidth={lineStrokeWidth}
                />
                <line
                  x1={isMobile ? '30' : '40'}
                  y1="6"
                  x2={isMobile ? '60' : '80'}
                  y2="6"
                  stroke="rgba(255, 0, 0, 0.3)"
                  strokeWidth={6}
                />
                <circle
                  cx={isMobile ? '30' : '40'}
                  cy="6"
                  r={dotRadius}
                  fill="rgba(0, 0, 0, 0.3)"
                  stroke="rgba(0, 0, 0, 0.3)"
                  strokeWidth={2}
                />
              </svg>
            </div>
          );

        case getChartOption(`${locationType} Rank`):
        case getChartOption(`${locationType} Percentile`):
          return (
            <div style={{ display: 'flex', alignItems: 'center' }}>
              <svg width="12" height="12">
                <circle
                  cx="6"
                  cy="6"
                  r={dotRadius}
                  fill="rgba(26, 44, 78, 0.6)"
                  stroke="rgba(26, 44, 78, 0.6)"
                  strokeWidth={2}
                />
              </svg>
            </div>
          );

        default:
          return (
            <svg width={isMobile ? '60' : '80'} height="12">
              <line
                x1="0"
                y1="6"
                x2={isMobile ? '60' : '80'}
                y2="6"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={lineStrokeWidth}
                strokeDasharray={selectedChart === `Previous Total` ? '6 6' : 'none'}
              />
              <circle
                cx={isMobile ? '30' : '40'}
                cy="6"
                r={dotRadius}
                fill="rgba(26, 44, 78, 0.6)"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={2}
              />
            </svg>
          );
      }
    };

    const getDisplayTitle = () => {
      if (selectedChart === `Total Crime`) {
        return `Total ${crimeName}`;
      } else if (selectedChart === `Total Crime vs Previous Total`) {
        return `Total ${crimeName}`;
      }
      return selectedChart;
    };

    return (
      <div style={{ 
        display: 'flex', 
        gap: '20px', 
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0 0 10px',
        position: 'relative'
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          {renderLegendIcon()}
          <div 
            ref={menuRef}
            style={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              gap: '4px',
              position: 'relative'
            }}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
          >
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>{getDisplayTitle()}</span>

            <ChevronDown className="chart-legend-chevron" style={{ width: '16px', height: '16px' }} />
          
            {isMenuOpen && (
              <div style={{
                position: 'absolute',
                top: '100%',
                left: getDropdownLeftPosition(selectedChart),
                backgroundColor: 'white',
                border: '1px solid var(--silver)',
                borderRadius: '4px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                zIndex: 1000,
                minWidth: 'max-content',
                marginTop: '4px',
                whiteSpace: 'nowrap'
              }}>
                {chartOptions.map((option, index) => {
                  const displayText = option.includes('Total Crime') ? 
                    option.replace('Crime', crimeName) : option;

                  return (
                    <div
                      key={option}
                      style={{
                        padding: '8px 12px',
                        cursor: selectedChart === option ? 'not-allowed' : 'pointer',
                        fontSize: isMobile ? '11px' : '12px',
                        fontWeight: 600,
                        textAlign: 'left',
                        backgroundColor: selectedChart === option ? 'var(--light-gray)' : 'transparent',
                        borderBottom: index < chartOptions.length - 1 ? '1px solid var(--border-color)' : 'none',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        position: 'relative',
                        boxSizing: 'border-box',
                        height: '31px'
                      }}
                      onMouseEnter={(e) => {
                        if (selectedChart !== option) {
                          e.currentTarget.style.backgroundColor = 'var(--silver)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (selectedChart !== option) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }
                      }}
                      onClick={(e) => {
                        if (selectedChart === option) {
                          e.stopPropagation();
                          return;
                        }
                        e.stopPropagation();
                        setSelectedChart(option);
                        onChartOptionChange(option);
                        setIsMenuOpen(false);
                      }}
                    >
                      <span style={{ opacity: selectedChart === option ? 0.5 : 1 }}>
                        {displayText}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
        {selectedChart === `Total Crime vs Previous Total` && (
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <svg width={isMobile ? '60' : '80'} height="12">
              <line
                x1="0"
                y1="6"
                x2={isMobile ? '60' : '80'}
                y2="6"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={2}
                strokeDasharray="6 6"
              />
              <circle
                cx={isMobile ? '30' : '40'}
                cy="6"
                r={4}
                fill="rgba(26, 44, 78, 0.6)"
                stroke="rgba(26, 44, 78, 0.6)"
                strokeWidth={2}
              />
            </svg>
            <span style={{ fontSize: isMobile ? '11px' : '12px', fontWeight: 600 }}>Previous Total</span>
          </div>
        )}
      </div>
    );
  }

  const renderCommonChartComponents = () => (
    <>
      <CartesianGrid stroke="var(--border-color)" strokeDasharray="none" />
      <XAxis 
        dataKey="year"
        axisLine={{ stroke: 'var(--border-color)' }}
        tickLine={{ stroke: 'var(--border-color)' }}
        tick={{ fontSize: isMobile ? 12 : 14, fontWeight: 400, fill: 'black' }}
        ticks={getCustomTicks()}
        domain={['dataMin', 'dataMax']}
        type="number"
        scale="linear"
        interval={0}
        allowDecimals={false}
        height={isMobile ? 20 : 30}
        tickFormatter={(value, index) => {
          const availableYears = getCustomTicks();
          const currentIndex = availableYears.indexOf(value);
          
          if (currentIndex === -1) return '';
          
          const totalYears = availableYears.length;
          
          let maxLabels;
          if (windowWidth <= 480) {
            maxLabels = 5; 
          } else if (windowWidth <= 767) {
            maxLabels = 7; 
          } else if (windowWidth <= 1023) {
            maxLabels = 9; 
          } else if (windowWidth <= 1300) {
            maxLabels = 7; 
          } else {
            maxLabels = 11; 
          }
          
          let minYearGap = 1;
          if (totalYears > 5) {
            if (windowWidth <= 480) {
              minYearGap = 3; 
            } else {
              minYearGap = 2; 
            }
          }
          
          if (currentIndex === 0 || currentIndex === totalYears - 1) {
            return value;
          }
          
          if (maxLabels <= 2) {
            return '';
          }
          
          const selectedIndices = [0, totalYears - 1];
          
          const additionalLabels = maxLabels - 2;
          for (let i = 1; i <= additionalLabels; i++) {
            const index = Math.round(((totalYears - 1) * i) / (additionalLabels + 1));
            
            const yearValue = availableYears[index];
            let meetsMinGap = true;
            
            for (const selectedIndex of selectedIndices) {
              const selectedYear = availableYears[selectedIndex];
              if (Math.abs(yearValue - selectedYear) < minYearGap) {
                meetsMinGap = false;
                break;
              }
            }
            
            if (meetsMinGap) {
              selectedIndices.push(index);
            }
          }
          
          return selectedIndices.includes(currentIndex) ? value : '';
        }}
      />
      <YAxis
        domain={getYAxisDomain()}
        ticks={getTicks(getMaxValue())}
        axisLine={{ stroke: 'var(--border-color)' }}
        tickLine={{ stroke: 'var(--border-color)' }}
        tickFormatter={(value, index) => {
          if (value === 0) return '0';

          const hasInfinity = chartData.some(item => 
            item.percentDiff === 'Infinity' || item.percentDiff === '-Infinity'
          );
      
          if (hasInfinity && selectedChart === `% Change from Previous Total`) {
            const maxVal = getMaxValue();
            if (Math.abs(value) >= maxVal * 0.9) {
              return value > 0 ? '+Inf%' : '-Inf%';
            }
          }

          if (selectedChart === getChartOption(`${locationType} Percentile`)) {
            return formatPercentile(value);
          } else if (selectedChart === getChartOption(`${locationType} Rank`)) {
            return formatRank(value);
          } else if (selectedChart.includes('Change')) {
            return formatDifference(value, selectedChart.includes('%'));
          } else {
            return formatNumber(value);
          }
        }}
        tick={props => {
          const { x, y, payload } = props;
          const domain = getYAxisDomain();
          const isBottomTick = payload.value === domain[0];
          const adjustedY = isBottomTick ? y - 5 : y;
          
          return (
            <text
              x={x}
              y={adjustedY}
              textAnchor="end"
              fill="black"
              fontSize={isMobile ? 12 : 14}
              fontWeight={400}
              dy={4}
            >
              {props.tickFormatter(payload.value)}
            </text>
          );
        }}
      />
      <Tooltip
        active={isMobile ? isTouching : undefined}
        content={({ active, payload, label }) => {
          if (active && payload && payload.length) {
            const data = payload[0].payload;
            const diffColor = getValueColor(data.difference);
            const percentDiffColor = getValueColor(data.percentDiff);
            
            const getRowStyle = (type) => ({
              display: 'flex', 
              justifyContent: 'space-between', 
              gap: '24px',
              backgroundColor: (
                (selectedChart === `Total Crime` && type === 'Total') ||
                (selectedChart === `Previous Total` && type === 'Previous Total') ||
                (selectedChart === `Change from Previous Total` && type === 'Change from Previous Total') ||
                (selectedChart === `% Change from Previous Total` && type === '% Change from Previous Total') ||
                (selectedChart === getChartOption(`${locationType} Rank`) && type === 'Rank') ||
                (selectedChart === getChartOption(`${locationType} Percentile`) && type === 'Percentile') ||
                (selectedChart === `Total Crime vs Previous Total` && 
                  (type === 'Total' || type === 'Previous Total' || type === 'Change from Previous Total'))
              ) ? '#fef9c3' : 'transparent',
              padding: '2px 4px',
              margin: '0 -4px'
            });
            
            if (data.total !== null) {
              return (
                <div className="custom-tooltip" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px',
                  border: '1px solid var(--silver)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    color: 'black',
                    textAlign: 'left',
                    paddingBottom: '2px',
                  }}>
                    {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                  </div>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 400,
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--silver)',
                    paddingBottom: '8px'
                  }}>
                    {data.year}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', display: 'grid', gap: '4px' }}>
                    <div style={getRowStyle('Total')}>
                      <span style={{ fontWeight: 600 }}>Total {crimeName}:</span>
                      <span>{formatNumber(data.total)}</span>
                    </div>
                    <div style={getRowStyle('Previous Total')}>
                      <span style={{ fontWeight: 600 }}>Previous Total:</span>
                      <span>{formatNumber(data.prevTotal)}</span>
                    </div>
                    <div style={getRowStyle('Change from Previous Total')}>
                      <span style={{ fontWeight: 600 }}>Change from Previous Total:</span>
                      <span style={{ color: `var(--${diffColor})` }}>
                        {formatDifference(data.difference)}
                      </span>
                    </div>
                    <div style={getRowStyle('% Change from Previous Total')}>
                      <span style={{ fontWeight: 600 }}>% Change from Previous Total:</span>
                      <span style={{ color: `var(--${percentDiffColor})` }}>
                        <strong>{formatDifference(data.percentDiff, true)}</strong>
                      </span>
                    </div>
                    {!isNation && (
                      <>
                        <div style={getRowStyle('Rank')}>
                          <span style={{ fontWeight: 600 }}>
                            {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Rank:
                          </span>
                          <span>{formatRank(data.rank)}</span>
                        </div>
                        <div style={getRowStyle('Percentile')}>
                          <span style={{ fontWeight: 600 }}>
                            {isCountyOrCity ? (compareMode === 'state' ? stateAbbr : 'US') : ''} {locationType} Percentile:
                          </span>
                          <span>{formatPercentile(data.percentile)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
              );
            } else {
              return (
                <div className="custom-tooltip" style={{
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  padding: '12px',
                  border: '1px solid var(--silver)',
                  borderRadius: '4px',
                  boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                }}>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 600,
                    fontSize: isMobile ? '13px' : '14px',
                    color: 'black',
                    textAlign: 'left',
                    paddingBottom: '2px',
                  }}>
                    {isCountyOrCity ? ((selectedLocation.info.name.split(',')[0]) + ', ' + stateAbbr) : selectedLocation.info.name}
                  </div>
                  <div style={{ 
                    margin: '0 0 4px 0',
                    fontWeight: 400,
                    fontSize: isMobile ? '13px' : '14px',
                    textAlign: 'left',
                    borderBottom: '1px solid var(--silver)',
                    paddingBottom: '8px'
                  }}>
                    {data.year}
                  </div>
                  <div style={{ fontSize: isMobile ? '11px' : '12px', padding: '20px' }}>
                    No data available
                  </div>
                </div>
              );
            }
          }
          return null;
        }}
      />
    </>
  );

  const getValue = (data) => {
    switch (selectedChart) {
      case `Total Crime`:
        return data.total;
      case `Previous Total`:
        return data.prevTotal;
      case `Total Crime vs Previous Total`:
        return {
          total: data.total,
          prevTotal: data.prevTotal
        };
      case `Change from Previous Total`:
        return data.difference;
      case `% Change from Previous Total`:
        return data.percentDiffForChart;
      case getChartOption(`${locationType} Rank`):
        return data.rankValue;
      case getChartOption(`${locationType} Percentile`):
        return data.percentile;
      default:
        return 0;
    }
  };
  
  return (
    <div className={`chart-container ${isMenuOpen ? 'active' : ''}`}>
      {renderCustomLegend()}
      <div 
        className="chart-content"
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onTouchCancel={handleTouchEnd}
      >
        <ResponsiveContainer>
          {selectedChart === `Total Crime vs Previous Total` ? (
            <AreaChart
              key="total-vs-previous" 
              data={chartData}
              margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? 20 : 30, bottom: 0 }}
            >
              {renderCommonChartComponents()}
              {(() => {
                const availableYears = chartData.filter(item => item.total !== null).sort((a, b) => a.year - b.year);
                const gapSegments = [];
                
                for (let i = 0; i < availableYears.length - 1; i++) {
                  const currentPoint = availableYears[i];
                  const nextPoint = availableYears[i + 1];
                  
                  if (nextPoint.year - currentPoint.year > 1) {
                    gapSegments.push(
                      <React.Fragment key={`gap-fragment-${currentPoint.year}-${nextPoint.year}`}>
                        <Area
                          key={`gap-total-${currentPoint.year}-${nextPoint.year}`}
                          type="monotone"
                          dataKey={(data) => {
                            if (data.year === currentPoint.year || data.year === nextPoint.year) {
                              return data.total;
                            }
                            return null;
                          }}
                          stroke="var(--border-color)"
                          strokeWidth={2}
                          strokeDasharray="none"
                          fill="var(--border-color)"
                          dot={false}
                          activeDot={false}
                          connectNulls={true}
                        />
                        <Area
                          key={`gap-prev-${currentPoint.year}-${nextPoint.year}`}
                          type="monotone"
                          dataKey={(data) => {
                            if (data.year === currentPoint.year || data.year === nextPoint.year) {
                              return data.prevTotal;
                            }
                            return null;
                          }}
                          stroke="var(--border-color)"
                          strokeWidth={2}
                          strokeDasharray="6 6"
                          fill="var(--border-color)"
                          dot={false}
                          activeDot={false}
                          connectNulls={true}
                        />  
                      </React.Fragment>
                    );
                  }
                }
                
                return gapSegments;
              })()}
              <Area
                dataKey={(data) => data.total === null ? 0 : getValue(data)}
                stroke="transparent"
                strokeWidth={0}
                dot={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke="none" 
                fill="rgba(255, 0, 0, 0.3)"
                dot={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="prevTotal"
                stroke="none"
                fill="rgba(0, 128, 0, 0.3)"
                dot={false}
                activeDot={false}
              />
              <Area
                type="monotone"
                dataKey="total"
                stroke="rgba(26, 44, 78, 0.6)"
                fill="none"
                strokeWidth={2}
                dot={false}
                activeDot={isMobile ? (isTouching ? {
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : false) : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                }}
              />
              <Area
                type="monotone"
                dataKey="prevTotal"
                stroke="rgba(26, 44, 78, 0.6)"
                fill="none"
                strokeWidth={2}
                strokeDasharray="6 6"
                dot={false}
                activeDot={isMobile ? (isTouching ? {
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : false) : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                }}
              />
            </AreaChart>
          ) : (selectedChart.includes('Change')) ? ( 
            <AreaChart
              data={chartData}
              margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? 20 : 30, bottom: 0 }}
            >
              {renderCommonChartComponents()}
              {(() => {
                const availableYears = chartData.filter(item => item.total !== null).sort((a, b) => a.year - b.year);
                const gapSegments = [];
                
                for (let i = 0; i < availableYears.length - 1; i++) {
                  const currentPoint = availableYears[i];
                  const nextPoint = availableYears[i + 1];
                  
                  if (nextPoint.year - currentPoint.year > 1) {
                    gapSegments.push(
                      <Area
                        key={`gap-${currentPoint.year}-${nextPoint.year}`}
                        type="monotone"
                        dataKey={(data) => {
                          if (data.year === currentPoint.year || data.year === nextPoint.year) {
                            return getValue(data);
                          }
                          return null;
                        }}
                        stroke="var(--border-color)"
                        strokeWidth={2}
                        strokeDasharray="none"
                        fill="var(--border-color)"
                        dot={false}
                        activeDot={false}
                        connectNulls={true}
                      />
                    );
                  }
                }
                
                return gapSegments;
              })()}
              <Area
                type="monotone"
                dataKey={(data) => {
                  const value = getValue(data);
                  return value > 0 ? value : 0;
                }}
                stroke="none"
                fill="rgba(255, 0, 0, 0.3)"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                baseValue={0}
              />
              <Area
                type="monotone"
                dataKey={(data) => {
                  const value = getValue(data);
                  return value < 0 ? value : 0;
                }}
                stroke="none"
                fill="rgba(0, 128, 0, 0.3)"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                baseValue={0}
              />
              <Area
                type="monotone"
                dataKey={(data) => getValue(data)}
                stroke="rgba(0, 0, 0, 0.3)"
                fill="none"
                strokeWidth={2}
                dot={false}
                activeDot={isMobile ? (isTouching ? {
                  r: 4, 
                  stroke: 'rgba(0, 0, 0, 0.3)'
                } : false) : { 
                  r: 4, 
                  stroke: 'rgba(0, 0, 0, 0.3)' 
                }}
              />
            </AreaChart>
          ) : (
            <LineChart
              data={chartData}
              margin={{ top: 0, right: isMobile ? 30 : 40, left: isMobile ? (selectedChart.includes('Rank') || selectedChart.includes('Percentile') ? -10 : 20) : 30, bottom: 0 }}
            >
              {renderCommonChartComponents()}
              {(() => {
                const availableYears = chartData.filter(item => item.total !== null).sort((a, b) => a.year - b.year);
                const gapSegments = [];
                
                for (let i = 0; i < availableYears.length - 1; i++) {
                  const currentPoint = availableYears[i];
                  const nextPoint = availableYears[i + 1];
                  
                  if (nextPoint.year - currentPoint.year > 1) {
                    gapSegments.push(
                      <Line
                        key={`gap-${currentPoint.year}-${nextPoint.year}`}
                        type="monotone"
                        dataKey={(data) => {
                          if (data.year === currentPoint.year || data.year === nextPoint.year) {
                            return getValue(data);
                          }
                          return null;
                        }}
                        stroke={selectedChart.includes('Rank') || selectedChart.includes('Percentile') ? 'transparent' : 'var(--border-color)'}
                        strokeWidth={2}
                        strokeDasharray={selectedChart === `Previous Total` ? '6 6' : 'none'}
                        dot={false}
                        activeDot={false}
                        connectNulls={true}
                      />
                    );
                  }
                }
                
                return gapSegments;
              })()}
              <Line 
                dataKey={(data) => data.total === null ? 0 : getValue(data)}
                stroke="transparent"
                strokeWidth={0}
                dot={false}
                activeDot={false}
              />
              <Line
                type="monotone"
                dataKey={(data) => getValue(data)}
                stroke={selectedChart.includes('Rank') || selectedChart.includes('Percentile') ? 'none' : 'rgba(26, 44, 78, 0.6)'}
                strokeWidth={2}
                strokeDasharray={selectedChart === `Previous Total` ? '6 6' : 'none'}
                dot={selectedChart.includes('Rank') || selectedChart.includes('Percentile') ? {
                  r: 4,
                  fill: 'var(--navy)',
                  fillOpacity: 0.6,
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : false}
                activeDot={isMobile ? (isTouching ? (selectedChart.includes('Rank') || selectedChart.includes('Percentile') ? {
                  r: 4,
                  fill: 'var(--navy)',
                  fillOpacity: 0.9,
                  stroke: 'rgba(26, 44, 78, 0.9)'
                } : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                }) : false) : (selectedChart.includes('Rank') || selectedChart.includes('Percentile') ? {
                  r: 4,
                  fill: 'var(--navy)',
                  fillOpacity: 0.6,
                  stroke: 'rgba(26, 44, 78, 0.6)'
                } : { 
                  r: 4, 
                  stroke: 'rgba(26, 44, 78, 0.6)' 
                })}
                animationDuration={selectedChart.includes('Rank') || selectedChart.includes('Percentile') ? 0 : 1000}
              />
            </LineChart>
          )}
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default HistoricalTrendsChart;